
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit3, Save, X, Clock } from "lucide-react";

interface ExtendedUserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  phone?: string;
  linkedin_url?: string;
  location_city?: string;
  location_state?: string;
  location_country?: string;
  timezone?: string;
  bio?: string;
  years_experience?: number;
  specializations?: string[];
  work_hours_start?: string;
  work_hours_end?: string;
  preferred_communication?: string;
}

interface PreferencesSectionProps {
  userProfile: ExtendedUserProfile;
  onUpdate: (updates: Partial<ExtendedUserProfile>) => Promise<void>;
}

const communicationOptions = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "text", label: "Text/SMS" },
  { value: "slack", label: "Slack" },
  { value: "teams", label: "Microsoft Teams" },
];

export const PreferencesSection: React.FC<PreferencesSectionProps> = ({
  userProfile,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    work_hours_start: userProfile.work_hours_start || "",
    work_hours_end: userProfile.work_hours_end || "",
    preferred_communication: userProfile.preferred_communication || "",
  });

  useEffect(() => {
    setFormData({
      work_hours_start: userProfile.work_hours_start || "",
      work_hours_end: userProfile.work_hours_end || "",
      preferred_communication: userProfile.preferred_communication || "",
    });
  }, [userProfile]);

  const handleSave = async () => {
    const updates: Partial<ExtendedUserProfile> = {};
    
    Object.keys(formData).forEach(key => {
      const typedKey = key as keyof typeof formData;
      if (formData[typedKey] !== (userProfile[typedKey] || "")) {
        updates[typedKey] = formData[typedKey] || undefined;
      }
    });

    if (Object.keys(updates).length > 0) {
      await onUpdate(updates);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      work_hours_start: userProfile.work_hours_start || "",
      work_hours_end: userProfile.work_hours_end || "",
      preferred_communication: userProfile.preferred_communication || "",
    });
    setIsEditing(false);
  };

  const formatWorkHours = () => {
    if (userProfile.work_hours_start && userProfile.work_hours_end) {
      return `${userProfile.work_hours_start} - ${userProfile.work_hours_end}`;
    }
    return "Not set";
  };

  const getCommunicationLabel = () => {
    const option = communicationOptions.find(opt => opt.value === userProfile.preferred_communication);
    return option ? option.label : "Not set";
  };

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="workStart">Work Hours Start</Label>
            <Input 
              id="workStart" 
              type="time"
              value={formData.work_hours_start} 
              onChange={(e) => setFormData({ ...formData, work_hours_start: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="workEnd">Work Hours End</Label>
            <Input 
              id="workEnd" 
              type="time"
              value={formData.work_hours_end} 
              onChange={(e) => setFormData({ ...formData, work_hours_end: e.target.value })}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="communication">Preferred Communication Method</Label>
          <Select value={formData.preferred_communication} onValueChange={(value) => setFormData({ ...formData, preferred_communication: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select preferred communication method" />
            </SelectTrigger>
            <SelectContent>
              {communicationOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Work Preferences</h3>
        <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
          <Edit3 className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>
      
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Label className="block text-muted-foreground text-sm mb-1">Work Hours</Label>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm">{formatWorkHours()}</p>
          </div>
        </div>
        <div>
          <Label className="block text-muted-foreground text-sm mb-1">Preferred Communication</Label>
          <p className="text-sm">{getCommunicationLabel()}</p>
        </div>
      </div>
    </div>
  );
};
